import * as React from 'react';
import { cn } from '@common/bw-ui/lib/utils';
import {
  AlertCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  InfoIcon,
} from '@common/bw-ui/icons';

export type AlertVariant = 'warning' | 'info' | 'error' | 'success';

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  description?: string;
  variant?: AlertVariant;
  icon?: React.ReactNode;
}

const variantConfig = {
  warning: {
    icon: ClockIcon,
    classes: 'tw:bg-warning-light tw:border-warning-border tw:text-warning',
    iconClass: 'tw:text-warning-icon',
    titleClass: 'tw:text-warning',
    descriptionClass: 'tw:text-warning-dark',
  },
  info: {
    icon: InfoIcon,
    classes: 'tw:bg-info-light tw:border-info/30 tw:text-info',
    titleClass: 'tw:text-info-dark',
    iconClass: 'tw:text-info-icon',
    descriptionClass: 'tw:text-info',
  },
  error: {
    icon: AlertCircleIcon,
    classes:
      'tw:bg-destructive-light tw:border-destructive/30 tw:text-destructive',
    titleClass: 'tw:text-destructive-dark',
    iconClass: 'tw:text-warning-icon',
    descriptionClass: 'tw:text-destructive/80',
  },
  success: {
    icon: CheckCircleIcon,
    classes: 'tw:bg-success-light tw:border-success/30 tw:text-success',
    titleClass: 'tw:text-success-dark',
    iconClass: 'tw:text-success-icon',
    descriptionClass: 'tw:text-success/80',
  },
} as const;

export function Alert({
  title,
  description,
  variant = 'warning',
  icon,
  className,
  ...props
}: AlertProps) {
  const config = variantConfig[variant];
  const DefaultIcon = config.icon;

  return (
    <div
      role="alert"
      className={cn(
        'tw:flex tw:items-center tw:gap-3 tw:rounded-lg tw:border tw:px-4 tw:py-3',
        config.classes,
        className,
      )}
      {...props}
    >
      <span
        data-slot="icon"
        className={cn(
          'tw:w-5 tw:h-5 tw:shrink-0 tw:flex tw:items-center tw:justify-center',
          config.iconClass,
        )}
        aria-hidden="true"
      >
        {icon ?? <DefaultIcon className="tw:w-5 tw:h-5" />}
      </span>
      <div data-slot="content" className="tw:flex tw:flex-col">
        {title !== undefined && title !== '' && (
          <span
            data-slot="title"
            className={cn('tw:text-sm tw:font-medium', config.titleClass)}
          >
            {title}
          </span>
        )}
        {description !== undefined && description !== '' && (
          <span
            data-slot="description"
            className={cn('tw:text-xs', config.descriptionClass)}
          >
            {description}
          </span>
        )}
      </div>
    </div>
  );
}
